import React, { useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "./AuthProvider";
import { Nav } from "../components/nav";
import { getPermission } from "../permissions/rolePermissions";
import { findFirstAccessibleRoute } from "../permissions/findFirstAccessibleRoute";
import AiSupportChat from "../components/support/AiSupportChat";

const spinnerStyle = {
  border: "16px solid #f3f3f3",
  borderTop: "16px solid #3498db",
  borderRadius: "50%",
  width: "60px",
  height: "60px",
  animation: "spin .5s linear infinite",
};

const spinnerContainerStyle = {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  height: "100vh",
};

const overlayStyle = {
  position: "fixed",
  top: 0,
  left: 250, // 👈 adjust to match Nav width
  right: 0,
  bottom: 0,
  background: "rgba(0,0,0,0.55)",
  backdropFilter: "blur(3px)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 9999,
};

const modalStyle = {
  background: "#fff",
  padding: "32px",
  borderRadius: "12px",
  boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
  textAlign: "center",
  maxWidth: "400px",
};

const buttonStyle = {
  marginTop: "16px",
  padding: "12px 20px",
  borderRadius: "8px",
  border: "none",
  background: "linear-gradient(135deg, #4facfe, #00f2fe)",
  color: "#fff",
  fontWeight: "600",
  cursor: "pointer",
};

const bannerStyle = {
  background: "#fff3cd",
  color: "#856404",
  padding: "10px 16px",
  textAlign: "center",
  fontSize: "14px",
  borderBottom: "1px solid #ffeeba",
};

const ProtectedRoute = ({ element, capability }) => {
  const { isAuthenticated, loading, user, company } = useAuth();
  const location = useLocation();

  // =========================
  // 💰 TRIAL LOGIC (NO GRACE)
  // =========================

  const trialEndsAt = company?.trial?.trialEndsAt
    ? new Date(company.trial.trialEndsAt)
    : null;

  const isTrial = company?.trial?.isTrial;
  const now = new Date();

  const isExpired = isTrial && trialEndsAt && now > trialEndsAt;
  // =========================
  // 💳 SUBSCRIPTION LOGIC
  // =========================

  const subscriptionStatus = company?.subscriptionStatus;
  const billingType = company?.billingType;

  // true om de betalar med kort men INTE har aktiv subscription
  const isSubscriptionInactive =
    billingType === "card" && subscriptionStatus !== "active";

  const daysLeft =
    trialEndsAt && !isExpired
      ? Math.ceil((trialEndsAt - now) / (1000 * 60 * 60 * 24))
      : 0;

  // Allow access to subscription-related pages
  const allowedWhenExpired = ["/subscription", "/billing", "/settings"];
  const isAllowedRoute = allowedWhenExpired.includes(location.pathname);

  const shouldBlock =
  (isExpired || isSubscriptionInactive) && !isAllowedRoute;

  // Lock scroll when blocked
  useEffect(() => {
    if (shouldBlock) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
  }, [shouldBlock]);

  // =========================
  // ⏳ Loading
  // =========================
  if (loading) {
    return (
      <div>
        <Nav />
        <div style={spinnerContainerStyle}>
          <div style={spinnerStyle} />
        </div>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  // 🔐 Not logged in
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // 🔐 Capability guard
  if (capability) {
    const permission = getPermission(user?.role, capability);

    if (permission === "none") {
      const fallbackPath = findFirstAccessibleRoute(user?.role);
      return <Navigate to={fallbackPath} replace />;
    }
  }

  // =========================
  // ✅ RENDER
  // =========================

  return (
    <div>
      <Nav />

      {/* ⚠️ WARNING: 7 days before expiry */}
      {!isExpired &&
      !isSubscriptionInactive &&
      daysLeft > 0 &&
      daysLeft <= 7 && (
        <div style={bannerStyle}>
          Din prenumeration avslutas om {daysLeft} dag{daysLeft === 1 ? "" : "ar"}.
          Du kommer då förlora åtkomst till systemet.
        </div>
      )}

      <div style={{ position: "relative" }}>
        {element}

        <AiSupportChat />

        {/* 🔒 HARD LOCK */}
        {shouldBlock && (
          <div style={overlayStyle}>
            <div style={modalStyle}>
              <h2>
                {isExpired
                  ? "Din testperiod har avslutats"
                  : "Din prenumeration är inte aktiv"}
              </h2>
              <p>
                {isExpired
                  ? "För att fortsätta använda systemet behöver du starta en prenumeration."
                  : "Din betalning har misslyckats eller prenumerationen har avslutats. För att fortsätta behöver du aktivera den igen."}
              </p>
              <button style={buttonStyle} onClick={()=> window.location.href = "/subscription"}>
                Starta prenumeration
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProtectedRoute;